import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { isNullish } from "remeda";
import { z } from "zod";

import TickIcon from "@/assets/icons/tick.svg?react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { ImportantNote } from "@/components/ui/importantNote";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { TextInput } from "@/components/ui/textInput";
import { Typography } from "@/components/ui/typography";
import { inputStep } from "@/constants/inputs";
import useCUCompositeJobStore from "@/store/useCUCompositeJobStore";
import { celsiusToFahrenheit, farenheitToCelcius, getFloat } from "@/utils/mappings";

import ExpandableSection from "../../../../components/job/ExpandableSection";
import { cn } from "../../../../lib/utils";

const cardName = "operatingConditions";
type OperatingConditionsProps = {
  temperatureUnitsOptions: { title: string; value: string }[];
};
export function OperatingConditions({ temperatureUnitsOptions }: OperatingConditionsProps) {
  const [unit_for_installation_temperature, setUnit_for_installation_temperature] = useState<string | null>(null);
  const [unit_for_operating_temp_temperature, setUnit_for_operating_temp_temperature] = useState<string | null>(null);
  const [dataToConfirm, setDataToConfirm] = useState<any>(null);

  const { sections, toggleExpandableSection, setOperatingConditions } = useCUCompositeJobStore();
  const savedOperatingConditions = sections.operatingConditions;

  const designPressure = sections.designConditions.data.design_pressure;
  const designTemperature = sections.designConditions.data.design_temperature;
  const designTemperatureUnit = sections.designConditions.data.unit_for_design_temperature;
  const shouldConvertedMaxTemp = designTemperatureUnit !== unit_for_operating_temp_temperature;
  let designTemperatureConverted = designTemperature;
  if (shouldConvertedMaxTemp && unit_for_operating_temp_temperature === "Celsius" && designTemperature) {
    designTemperatureConverted = farenheitToCelcius(designTemperature);
  } else if (shouldConvertedMaxTemp && unit_for_operating_temp_temperature === "Fahrenheit" && designTemperature) {
    designTemperatureConverted = celsiusToFahrenheit(designTemperature);
  }

  const operatingConditionsSchema = z.object({
    operating_pressure: z
      .number({ error: "Number is expected" })
      .nullable()
      .optional()
      .refine((value) => {
        return (
          isNullish(value) ||
          (value !== null && designPressure != null && !isNullish(value) && value < Number(designPressure))
        );
      }, "Operating pressure should be less than design pressure"),
    operating_temp_temperature: z
      .number({ error: "Number is expected" })
      .nullable()
      .optional()
      .refine((value) => {
        return (
          isNullish(value) ||
          (value !== null &&
            designTemperatureConverted != null &&
            !isNullish(value) &&
            value < Number(designTemperatureConverted))
        );
      }, "Operating temperature should be less than design temperature"),
    installation_temperature: z.number({ error: "Number is expected" }).nullable().optional(),
  });

  type OperatingConditionsSchema = z.infer<typeof operatingConditionsSchema>;

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<OperatingConditionsSchema>({
    resolver: zodResolver(operatingConditionsSchema),
    values: {
      operating_pressure: savedOperatingConditions.data.operating_pressure,
      operating_temp_temperature: savedOperatingConditions.data.operating_temp_temperature,
      installation_temperature: savedOperatingConditions.data.installation_temperature,
    },
  });

  useEffect(() => {
    if (!unit_for_installation_temperature) {
      const savedUnit = savedOperatingConditions.data.unit_for_installation_temperature;
      if (savedUnit || savedUnit === "Fahrenheit") {
        setUnit_for_installation_temperature(savedUnit || "Fahrenheit");
      } else {
        setUnit_for_installation_temperature("Fahrenheit");
      }
    }

    if (!unit_for_operating_temp_temperature) {
      const savedUnit = savedOperatingConditions.data.unit_for_operating_temp_temperature;
      if (savedUnit || savedUnit === "Fahrenheit") {
        setUnit_for_operating_temp_temperature(savedUnit || "Fahrenheit");
      } else {
        setUnit_for_operating_temp_temperature("Fahrenheit");
      }
    }
  }, [
    savedOperatingConditions.data.unit_for_installation_temperature,
    savedOperatingConditions.data.unit_for_operating_temp_temperature,
  ]);

  const handleSaveOperatingConditions = () => {
    setDataToConfirm(null);
    setOperatingConditions({
      data: {
        ...dataToConfirm,
        unit_for_operating_temp_temperature,
        unit_for_installation_temperature,
      },
      status: "done",
    });
  };

  return (
    <>
      <ExpandableSection
        setIsExpanded={() => toggleExpandableSection(cardName)}
        status={savedOperatingConditions.status}
        title="Operating Conditions"
      >
        <form
          onSubmit={handleSubmit((data) => {
            setDataToConfirm(data);
          })}
          className="flex flex-col gap-2"
        >
          <div className="flex flex-wrap gap-2">
            <TextInput
              className="min-w-[255px] flex-1 "
              label="Operating Pressure"
              type="number"
              step={inputStep}
              error={errors.operating_pressure?.message}
              {...register("operating_pressure", { setValueAs: getFloat })}
              endAdornment={
                <Typography variant="text-small" weight="regular" className="text-gray-400">
                  psi
                </Typography>
              }
            />
            <TextInput
              className="min-w-[255px] flex-1"
              label="Operating Temp Temperature"
              type="number"
              step={inputStep}
              error={errors.operating_temp_temperature?.message}
              {...register("operating_temp_temperature", { setValueAs: getFloat })}
            />
            <div className="min-w-[255px] flex-1">
              <Label>Unit for Operating Temp Temperature</Label>
              <Select
                value={unit_for_operating_temp_temperature ?? undefined}
                onValueChange={(option) => {
                  setUnit_for_operating_temp_temperature(option);
                }}
              >
                <SelectTrigger className={cn(!unit_for_operating_temp_temperature && "[&>span]:text-gray-400")}>
                  <SelectValue placeholder="Unit for Operating Temp Temperature" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Unit for Operating Temp Temperature</SelectLabel>
                    {temperatureUnitsOptions.map((option) => {
                      return (
                        <SelectItem key={option.value} value={option.value}>
                          {option.title}
                        </SelectItem>
                      );
                    })}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <TextInput
              className="min-w-[255px] flex-1"
              label="Installation Temperature"
              type="number"
              step={inputStep}
              error={errors.installation_temperature?.message}
              {...register("installation_temperature", { setValueAs: getFloat })}
            />
            <div className="min-w-[255px] flex-1">
              <Label>Unit for Installation Temperature</Label>
              <Select
                value={unit_for_installation_temperature ?? undefined}
                onValueChange={(option) => {
                  setUnit_for_installation_temperature(option);
                }}
              >
                <SelectTrigger
                  className={cn(
                    "min-w-[255px] flex-1",
                    !unit_for_operating_temp_temperature && "[&>span]:text-gray-400"
                  )}
                >
                  <SelectValue placeholder="Unit for Installation Temperature" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Unit for Installation Temperature</SelectLabel>
                    {temperatureUnitsOptions.map((option) => {
                      return (
                        <SelectItem key={option.value} value={option.value}>
                          {option.title}
                        </SelectItem>
                      );
                    })}
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button className="self-end" type="submit">
            Save
          </Button>
        </form>
      </ExpandableSection>
      <Dialog open={!!dataToConfirm} onOpenChange={() => setDataToConfirm(null)}>
        <DialogContent>
          <DialogTitle className="sr-only">Confirm operating conditions</DialogTitle>
          <DialogDescription className="sr-only">
            Confirm the operating pressure, temperatures, and installation values before saving.
          </DialogDescription>
          <div className="flex w-[300px] flex-col md:w-[400px]">
            <div className="mb-4 flex justify-between">
              <div className="bg w flex h-12 w-12 items-center justify-center rounded-full bg-success-50">
                <div className="bg w flex h-8 w-8 items-center justify-center rounded-full bg-success-100">
                  <TickIcon className="h-[11px] w-[16px]" />
                </div>
              </div>
            </div>
            <ImportantNote>Please confirm the details below are correct</ImportantNote>
            {dataToConfirm ? (
              <div className="flex flex-col gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <Typography variant="text-md" weight="semibold" className="text-gray-800">
                    Operating Pressure:
                  </Typography>
                  <Typography variant="text-md" className="text-gray-600">
                    {dataToConfirm.operating_pressure ?? "N/A"}(psi)
                  </Typography>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Typography variant="text-md" weight="semibold" className="text-gray-800">
                    Operating Temp Temperature:
                  </Typography>
                  <Typography variant="text-md" className="text-gray-600">
                    {dataToConfirm.operating_temp_temperature ?? "N/A"}({unit_for_operating_temp_temperature})
                  </Typography>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Typography variant="text-md" weight="semibold" className="text-gray-800">
                    Installation Temperature:
                  </Typography>
                  <Typography variant="text-md" className="text-gray-600">
                    {dataToConfirm.installation_temperature ?? "N/A"}({unit_for_installation_temperature})
                  </Typography>
                </div>
              </div>
            ) : null}
            <div className="mt-8 flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setDataToConfirm(null)}>
                Cancel
              </Button>
              <Button className="flex-1" onClick={handleSaveOperatingConditions}>
                Confirm
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
